import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Button } from "../Button";
import { ReactMailQuill } from "./ReactMailQuill";
import mailClientAPI from "../../api/mail-client-api";
import md5 from "md5";
import { notification } from "antd";


// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Parse comma-separated emails
const parseEmails = (emailString) => {
  if (!emailString || !emailString.trim()) return [];
  return emailString
    .split(",")
    .map((email) => email.trim())
    .filter((email) => email.length > 0);
};

// Validate email addresses
const validateEmails = (emailString) => {
  const emails = parseEmails(emailString);
  return emails.every((email) => EMAIL_REGEX.test(email));
};

// Format emails for display (show count if more than 1)
const formatEmailDisplay = (emailString) => {
  const emails = parseEmails(emailString);
  if (emails.length === 0) return "";
  if (emails.length === 1) return emails[0];
  return `${emails.length} recipients`;
};

// Generate Gravatar URL from email
const getGravatarUrl = (email, size = 20) => {
  if (!email) return null;
  const emailHash = md5(email.toLowerCase().trim());
  return `https://www.gravatar.com/avatar/${emailHash}?s=${size}&d=404`;
};

// Email Chip Component
const EmailChip = ({ email, onRemove }) => {
  const [gravatarError, setGravatarError] = useState(false);
  const gravatarUrl = getGravatarUrl(email, 20);
  const initials = email ? email.charAt(0).toUpperCase() : "?";

  return (
    <div className="inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-full p-[2px] text-xs font-medium border border-gray-300">
      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center overflow-hidden flex-shrink-0">
        {gravatarUrl && !gravatarError ? (
          <img
            src={gravatarUrl}
            alt={email}
            className="w-full h-full object-cover"
            onError={() => setGravatarError(true)}
          />
        ) : (
          <span className="material-icons-outlined text-xs text-blue-600">person</span>
        )}
      </div>
      <span>{email}</span>
      <button
        type="button"
        onClick={onRemove}
        className="flex ml-0.5 hover:bg-gray-200 rounded-full p-0.5 transition-colors flex items-center justify-center"
        aria-label={`Remove ${email}`}
      >
        <span className="material-icons-outlined text-sm text-gray-500">close</span>
      </button>
    </div>
  );
};

// Email Input Component with Chips
const EmailInput = ({ 
  emails, 
  setEmails, 
  placeholder, 
  maxEmails = Infinity,
  showCount = false,
  inputRef = null
}) => {
  const [inputValue, setInputValue] = useState("");

  const addEmail = (email) => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !EMAIL_REGEX.test(trimmedEmail)) {
      return false;
    }
    
    // Check for duplicates (case-insensitive)
    const isDuplicate = emails.some((e) => e.email.toLowerCase() === trimmedEmail.toLowerCase());
    if (isDuplicate) {
      return false;
    }

    // Check max emails limit
    if (emails.length >= maxEmails) {
      return false;
    }

    setEmails([...emails, { id: Date.now() + Math.random(), email: trimmedEmail }]);
    return true;
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Check for comma to create chip
    if (value.includes(",")) {
      const parts = value.split(",");
      const emailToAdd = parts[0].trim();
      if (emailToAdd) {
        if (addEmail(emailToAdd)) {
          // Email added successfully, process remaining parts
          const remaining = parts.slice(1).join(",").trim();
          setInputValue(remaining);
          // If there are more parts, recursively process them
          if (remaining.includes(",")) {
            setTimeout(() => {
              const event = { target: { value: remaining } };
              handleInputChange(event);
            }, 0);
          }
        } else {
          // Invalid or duplicate email, keep it in input
          setInputValue(value);
        }
      } else {
        // Empty before comma, just remove the comma
        setInputValue(parts.slice(1).join(",").trim());
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedValue = inputValue.trim();
      if (trimmedValue) {
        if (addEmail(trimmedValue)) {
          setInputValue("");
        }
      }
    } else if (e.key === "Backspace" && inputValue === "" && emails.length > 0) {
      // Remove last chip on backspace when input is empty
      setEmails(emails.slice(0, -1));
    }
  };

  const handleBlur = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue) {
      // Try to add the email if it's valid
      if (addEmail(trimmedValue)) {
        setInputValue("");
      } else {
        // If invalid or duplicate, clear the input on blur
        setInputValue("");
      }
    }
  };

  const removeEmail = (id) => {
    setEmails(emails.filter((e) => e.id !== id));
  };

  return (
    <div className="flex-1 flex items-center flex-wrap gap-1.5 min-h-[32px] py-1">
      {emails.map((emailObj) => (
        <EmailChip
          key={emailObj.id}
          email={emailObj.email}
          onRemove={() => removeEmail(emailObj.id)}
        />
      ))}
      <input
        ref={inputRef}
        type="text"
        className="flex-1 min-w-[120px] outline-none text-sm py-1 bg-transparent"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={emails.length === 0 ? placeholder : ""}
      />
      {showCount && emails.length > 0 && (
        <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
          {emails.length}/{maxEmails}
        </span>
      )}
    </div>
  );
};

export const ComposeModal = ({ isOpen, onClose, draftId = null, draftMailbox = null, draftEmailId = null, onDraftSaved = null, onDraftDeleted = null, updateMailboxCounts = null }) => {
  const [toEmails, setToEmails] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [bccEmails, setBccEmails] = useState([]);
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
  const [sending, setSending] = useState(false);
  const [isAiMode, setIsAiMode] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [showSchedulePopover, setShowSchedulePopover] = useState(false);
  const [scheduledDateTime, setScheduledDateTime] = useState("");
  const [scheduling, setScheduling] = useState(false);
  // Track current draft ID state (separate from props to persist across saves)
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [currentDraftMailbox, setCurrentDraftMailbox] = useState(null);
  const [currentDraftEmailId, setCurrentDraftEmailId] = useState(null);
  const fileInputRef = useRef(null);
  const toInputRef = useRef(null);
  const ccInputRef = useRef(null);
  const bccInputRef = useRef(null);
  const quillRef = useRef(null);
  const schedulePopoverRef = useRef(null);

  const MAX_SUBJECT_LENGTH = 255;
  const MAX_CC_BCC_EMAILS = 10;

  // Load draft data when opening a draft
  useEffect(() => {
    if (isOpen && draftId && draftMailbox && draftEmailId) {
      const loadDraft = async () => {
        try {
          const response = await mailClientAPI.getDraft(draftMailbox, draftEmailId);
          const draft = response.Output;
          
          // Check if draft was already sent (if API provides this information)
          // Common properties that might indicate a draft was sent: sent, isSent, status === 'sent', etc.
          const isSent = draft.sent === true || draft.isSent === true || draft.status === 'sent';
          
          if (isSent) {
            // Draft was already sent, delete it and close the modal
            try {
              await mailClientAPI.deleteDraft(draftMailbox, draftEmailId);
              // Notify parent component to remove from local state
              if (onDraftDeleted) {
                onDraftDeleted(draftMailbox, draftEmailId);
              }
              notification.info({
                message: "Draft already sent",
                description: "This draft was already sent and has been removed.",
                placement: "bottomLeft",
                duration: 3,
              });
            } catch (deleteErr) {
              console.error("Error deleting sent draft:", deleteErr);
            }
            // Close the modal
            onClose();
            return;
          }
          
          // Parse emails
          const toList = (draft.to || []).map(email => ({ id: Date.now() + Math.random(), email }));
          const ccList = (draft.cc || []).map(email => ({ id: Date.now() + Math.random(), email }));
          const bccList = (draft.bcc || []).map(email => ({ id: Date.now() + Math.random(), email }));
          
          setToEmails(toList);
          setCcEmails(ccList);
          setBccEmails(bccList);
          setShowCcBcc(ccList.length > 0 || bccList.length > 0);
          setSubject(draft.subject || "");
          setBody(draft.html || draft.body || "");
          
          // Set current draft state from props
          setCurrentDraftId(draftId);
          setCurrentDraftMailbox(draftMailbox);
          setCurrentDraftEmailId(draftEmailId);
        } catch (err) {
          console.error("Error loading draft:", err);
          // If draft doesn't exist (404), it might have been deleted after being sent
          if (err.response?.status === 404) {
            notification.info({
              message: "Draft not found",
              description: "This draft may have been deleted after the email was sent.",
              placement: "bottomLeft",
              duration: 3,
            });
            // Notify parent component to remove from local state
            if (onDraftDeleted) {
              onDraftDeleted(draftMailbox, draftEmailId);
            }
            onClose();
          } else {
            notification.error({
              message: "Failed to load draft",
              description: err.response?.data?.Output?.message || err.message,
              placement: "bottomLeft",
              duration: 3,
            });
          }
        }
      };
      loadDraft();
    } else if (isOpen && !draftId) {
      // Reset form when opening new compose (not a draft)
      setToEmails([]);
      setCcEmails([]);
      setBccEmails([]);
      setShowCcBcc(false);
      setSubject("");
      setBody("");
      setAttachments([]);
      // Clear draft state
      setCurrentDraftId(null);
      setCurrentDraftMailbox(null);
      setCurrentDraftEmailId(null);
      setDraftSaved(false);
    }
  }, [isOpen, draftId, draftMailbox, draftEmailId, onClose, onDraftDeleted]);

  // Prevent browser close/refresh if there's content in the compose modal
  useEffect(() => {
    if (!isOpen) return;

    const hasContent = subject.trim() || body.trim() || toEmails.length > 0;
    
    if (hasContent) {
      const handleBeforeUnload = (e) => {
        // Modern browsers require returnValue to be set
        e.preventDefault();
        e.returnValue = '';
        return '';
      };

      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
  }, [isOpen, subject, body, toEmails]);

  // Save draft function
  const handleSaveDraft = async (silent = false) => {
    // Only save if there's actual content
    const hasContent = subject.trim() || body.trim() || toEmails.length > 0;
    if (!hasContent) {
      return;
    }

    setSavingDraft(true);
    try {
      const to = toEmails.map((e) => e.email).join(",");
      const cc = ccEmails.length > 0 ? ccEmails.map((e) => e.email).join(",") : "";
      const bcc = bccEmails.length > 0 ? bccEmails.map((e) => e.email).join(",") : "";

      const formData = new FormData();
      formData.append("to", to);
      if (cc) formData.append("cc", cc);
      if (bcc) formData.append("bcc", bcc);
      formData.append("subject", subject || "(No Subject)");
      formData.append("body", body);
      formData.append("html", body);
      
      // Include draft info if updating existing draft (use current state, not props)
      if (currentDraftId && currentDraftMailbox && currentDraftEmailId) {
        formData.append("draftId", currentDraftId);
        formData.append("mailbox", currentDraftMailbox);
        formData.append("emailId", currentDraftEmailId);
      }

      // Add attachments
      attachments.forEach((att) => {
        formData.append("attachments", att.file);
      });

      const response = await mailClientAPI.saveDraft(formData);
      
      // Update current draft state with the response (important for new drafts)
      // This ensures subsequent saves update the same draft instead of creating new ones
      const draftInfo = {
        uid: response.Output?.uid,
        mailbox: response.Output?.mailbox,
        subject: subject || "(No Subject)",
        to: to.split(",").filter(Boolean),
        cc: cc ? cc.split(",").filter(Boolean) : [],
        bcc: bcc ? bcc.split(",").filter(Boolean) : [],
        body: body,
        html: body,
        preview: body.replace(/<[^>]*>/g, '').substring(0, 100), // Simple HTML strip for preview
      };

      if (response.Output?.uid && response.Output?.mailbox) {
        setCurrentDraftId(response.Output.uid.toString());
        setCurrentDraftMailbox(response.Output.mailbox);
        setCurrentDraftEmailId(response.Output.uid.toString());
      }
      
      // Notify parent component to update local state
      // Pass old draft ID if we're updating an existing draft
      if (onDraftSaved) {
        const oldDraftId = currentDraftId && currentDraftMailbox && currentDraftEmailId && 
                          (currentDraftId !== response.Output?.uid?.toString()) 
                          ? currentDraftEmailId : null;
        onDraftSaved(draftInfo, oldDraftId);
        
        // Increase draft count if this is a new draft (not updating an existing one)
        if (!oldDraftId && updateMailboxCounts && response.Output?.mailbox) {
          updateMailboxCounts({
            destinationFolder: response.Output.mailbox,
            count: 1,
            wasUnread: false, // Drafts are typically marked as read
          });
        }
      }
      
      if (!silent) {
        setDraftSaved(true);
        notification.success({
          message: "Draft saved",
          placement: "bottomLeft",
          duration: 2,
        });
      }
    } catch (err) {
      console.error("Error saving draft:", err);
      if (!silent) {
        notification.error({
          message: "Failed to save draft",
          description: err.response?.data?.Output?.message || err.message,
          placement: "bottomLeft",
          duration: 3,
        });
      }
    } finally {
      setSavingDraft(false);
    }
  };

  // Handle close with draft save
  const handleClose = () => {
    // Save draft in background if there's content (don't await)
    const hasContent = subject.trim() || body.trim() || toEmails.length > 0;
    if (hasContent) {
      // Save draft in background and show notification when done
      handleSaveDraft(true)
        .then(() => {
          // Show toast notification after saving
          notification.success({
            message: "Draft saved",
            description: "Your draft has been saved automatically.",
            placement: "bottomLeft",
            duration: 3,
          });
        })
        .catch((err) => {
          // Silently handle errors in background save
          console.error("Error saving draft on close:", err);
        });
    }

    // Reset state immediately
    setToEmails([]);
    setCcEmails([]);
    setBccEmails([]);
    setShowCcBcc(false);
    setSubject("");
    setBody("");
    setAttachments([]);
    setError(null);
    setDraftSaved(false);
    // Clear draft state
    setCurrentDraftId(null);
    setCurrentDraftMailbox(null);
    setCurrentDraftEmailId(null);
    
    // Hide modal instantly
    onClose();
  };

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    if (value.length <= MAX_SUBJECT_LENGTH) {
      setSubject(value);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newAttachments = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
    }));
    setAttachments([...attachments, ...newAttachments]);
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeAttachment = (id) => {
    setAttachments(attachments.filter((att) => att.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
  };

  const handleSend = async () => {
    setError(null);

    // Validation
    if (toEmails.length === 0) {
      setError("Please enter at least one recipient");
      return;
    }

    if (ccEmails.length > MAX_CC_BCC_EMAILS) {
      setError(`Maximum ${MAX_CC_BCC_EMAILS} emails allowed in Cc`);
      return;
    }

    if (bccEmails.length > MAX_CC_BCC_EMAILS) {
      setError(`Maximum ${MAX_CC_BCC_EMAILS} emails allowed in Bcc`);
      return;
    }

    setSending(true);

    try {
      // Convert email arrays to comma-separated strings
      const to = toEmails.map((e) => e.email).join(",");
      const cc = ccEmails.length > 0 ? ccEmails.map((e) => e.email).join(",") : "";
      const bcc = bccEmails.length > 0 ? bccEmails.map((e) => e.email).join(",") : "";

      // Prepare form data for sending
      const formData = new FormData();
      formData.append("to", to);
      if (cc) formData.append("cc", cc);
      if (bcc) formData.append("bcc", bcc);
      formData.append("subject", subject || "(No Subject)");
      formData.append("body", body);
      formData.append("html", body);

      // Add attachments
      attachments.forEach((att) => {
        formData.append("attachments", att.file);
      });

      await mailClientAPI.sendEmail(formData);

      // Delete draft if it was being edited (use current state, not props)
      if (currentDraftId && currentDraftMailbox && currentDraftEmailId) {
        try {
          await mailClientAPI.deleteDraft(currentDraftMailbox, currentDraftEmailId);
          // Notify parent component to remove from local state
          if (onDraftDeleted) {
            onDraftDeleted(currentDraftMailbox, currentDraftEmailId);
          }
        } catch (deleteErr) {
          console.error("Error deleting draft after send:", deleteErr);
          // Don't fail the send operation if draft deletion fails
        }
      }

      // Show success notification
      notification.success({
        message: "Email sent successfully",
        description: `Your email has been sent to ${toEmails.length} recipient${toEmails.length > 1 ? "s" : ""}.`,
        placement: "bottomLeft",
        duration: 3,
      });

      // Reset form
      setToEmails([]);
      setCcEmails([]);
      setBccEmails([]);
      setSubject("");
      setBody("");
      setAttachments([]);
      setShowCcBcc(false);
      setError(null);
      setDraftSaved(false);
      onClose();
    } catch (err) {
      console.error("Error sending email:", err);
      setError(err.response?.data?.Output?.message || err.message || "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async () => {
    setError(null);

    // Validation
    if (toEmails.length === 0) {
      setError("Please enter at least one recipient");
      return;
    }

    if (ccEmails.length > MAX_CC_BCC_EMAILS) {
      setError(`Maximum ${MAX_CC_BCC_EMAILS} emails allowed in Cc`);
      return;
    }

    if (bccEmails.length > MAX_CC_BCC_EMAILS) {
      setError(`Maximum ${MAX_CC_BCC_EMAILS} emails allowed in Bcc`);
      return;
    }

    if (!scheduledDateTime) {
      setError("Please select a date and time to schedule the email");
      return;
    }

    const scheduledDate = new Date(scheduledDateTime);
    if (scheduledDate <= new Date()) {
      setError("Scheduled time must be in the future");
      return;
    }

    setScheduling(true);

    try {
      // Convert email arrays to comma-separated strings
      const to = toEmails.map((e) => e.email).join(",");
      const cc = ccEmails.length > 0 ? ccEmails.map((e) => e.email).join(",") : "";
      const bcc = bccEmails.length > 0 ? bccEmails.map((e) => e.email).join(",") : "";

      // Prepare form data for scheduling
      const formData = new FormData();
      formData.append("to", to);
      if (cc) formData.append("cc", cc);
      if (bcc) formData.append("bcc", bcc);
      formData.append("subject", subject || "(No Subject)");
      formData.append("body", body);
      formData.append("html", body);
      formData.append("scheduledAt", scheduledDate.toISOString());

      // Add attachments
      attachments.forEach((att) => {
        formData.append("attachments", att.file);
      });

      await mailClientAPI.scheduleEmail(formData);

      // Delete draft if it was being edited
      if (currentDraftId && currentDraftMailbox && currentDraftEmailId) {
        try {
          await mailClientAPI.deleteDraft(currentDraftMailbox, currentDraftEmailId);
          if (onDraftDeleted) {
            onDraftDeleted(currentDraftMailbox, currentDraftEmailId);
          }
        } catch (deleteErr) {
          console.error("Error deleting draft after schedule:", deleteErr);
        }
      }

      // Show success notification
      notification.success({
        message: "Email scheduled successfully",
        description: `Your email will be sent on ${scheduledDate.toLocaleString()}.`,
        placement: "bottomLeft",
        duration: 3,
      });

      // Reset form
      setToEmails([]);
      setCcEmails([]);
      setBccEmails([]);
      setSubject("");
      setBody("");
      setAttachments([]);
      setShowCcBcc(false);
      setError(null);
      setDraftSaved(false);
      setScheduledDateTime("");
      setShowSchedulePopover(false);
      onClose();
    } catch (err) {
      console.error("Error scheduling email:", err);
      setError(err.response?.data?.Output?.message || err.message || "Failed to schedule email");
    } finally {
      setScheduling(false);
    }
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (schedulePopoverRef.current && !schedulePopoverRef.current.contains(event.target)) {
        setShowSchedulePopover(false);
      }
    };

    if (showSchedulePopover) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showSchedulePopover]);

  // Quill modules configuration for custom toolbar
  const modules = useMemo(() => {
    return {
      toolbar: {
        container: "#rich-area-text-effect-compose",
      },
    };
  }, []);

  const handleAiWrite = async () => {
    if (!aiPrompt || !aiPrompt.trim()) return;

    setIsGenerating(true);
    setError(null);

    try {
      // Convert email arrays to comma-separated strings
      const to = toEmails.length > 0 ? toEmails.map((e) => e.email).join(",") : "";
      const cc = ccEmails.length > 0 ? ccEmails.map((e) => e.email).join(",") : "";
      const bcc = bccEmails.length > 0 ? bccEmails.map((e) => e.email).join(",") : "";

      const response = await mailClientAPI.composeWithAI({
        to: to || undefined,
        cc: cc || undefined,
        bcc: bcc || undefined,
        subject: subject || undefined,
        context: aiPrompt,
      });

      if (response.Output?.subject) {
        setSubject(response.Output.subject);
      }
      if (response.Output?.body) {
        setBody(response.Output.body);
      }

      // Close AI mode and clear prompt after successful generation
      setIsAiMode(false);
      setAiPrompt("");
    } catch (err) {
      console.error("Error composing with AI:", err);
      setError(err.response?.data?.Output?.message || err.message || "Failed to compose with AI");
    } finally {
      setIsGenerating(false);
    }
  };

  if (!isOpen) return null;


  return (
    <div
      className="fixed inset-0 md:inset-auto md:bottom-0 md:right-20 md:w-[600px] w-full h-full md:h-[600px] bg-white rounded-none md:rounded-t-lg shadow-2xl md:border border-gray-200 flex flex-col z-50 overflow-hidden font-sans"
      style={{ boxShadow: "0 0 24px rgba(0,0,0,0.15)" }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#1a1a1a] text-white rounded-none md:rounded-t-lg">
        <span className="font-medium text-sm">New Message</span>
        <div className="flex items-center space-x-3">
          <button className="text-gray-400 hover:text-white flex items-center" onClick={handleClose}>
            <span className="material-icons-outlined text-base">close</span>
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Fields */}
      <div className="flex flex-col flex-1 relative overflow-hidden">
        <div className="px-4 py-2 border-b border-gray-100 flex items-center group transition-colors focus-within:bg-gray-50">
          <span className="text-gray-500 text-sm w-10">To</span>
          <EmailInput
            emails={toEmails}
            setEmails={setToEmails}
            placeholder="Recipients"
            inputRef={toInputRef}
          />
          {!showCcBcc && (
            <button
              onClick={() => setShowCcBcc(true)}
              className="text-xs text-gray-500 hover:text-gray-900 ml-2"
            >
              Cc / Bcc
            </button>
          )}
        </div>

        {showCcBcc && (
          <>
            <div className="px-4 py-2 border-b border-gray-100 flex items-center animate-fade-in focus-within:bg-gray-50">
              <span className="text-gray-500 text-sm w-10">Cc</span>
              <EmailInput
                emails={ccEmails}
                setEmails={setCcEmails}
                placeholder="Cc"
                maxEmails={MAX_CC_BCC_EMAILS}
                showCount={true}
                inputRef={ccInputRef}
              />
            </div>
            <div className="px-4 py-2 border-b border-gray-100 flex items-center animate-fade-in focus-within:bg-gray-50">
              <span className="text-gray-500 text-sm w-10">Bcc</span>
              <EmailInput
                emails={bccEmails}
                setEmails={setBccEmails}
                placeholder="Bcc"
                maxEmails={MAX_CC_BCC_EMAILS}
                showCount={true}
                inputRef={bccInputRef}
              />
            </div>
          </>
        )}

        <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center focus-within:bg-gray-50">
          <input
            type="text"
            placeholder="Subject"
            className="flex-1 outline-none text-sm py-1 font-medium bg-transparent placeholder-gray-500"
            value={subject}
            onChange={handleSubjectChange}
            maxLength={MAX_SUBJECT_LENGTH}
          />
          <span className={`text-xs ml-2 ${subject.length >= MAX_SUBJECT_LENGTH ? "text-red-500" : "text-gray-400"}`}>
            {subject.length}/{MAX_SUBJECT_LENGTH}
          </span>
        </div>

        {/* Attachments */}
        {attachments.length > 0 && (
          <div className="px-4 py-2 border-b border-gray-100 bg-gray-50 flex flex-wrap gap-2">
            {attachments.map((att) => (
              <div
                key={att.id}
                className="flex items-center gap-2 bg-white border border-gray-200 rounded px-2 py-1 text-xs"
              >
                <span className="material-icons-outlined text-sm text-gray-500">attach_file</span>
                <span className="text-gray-700 max-w-[150px] truncate">{att.name}</span>
                <span className="text-gray-400">({formatFileSize(att.size)})</span>
                <button
                  onClick={() => removeAttachment(att.id)}
                  className="text-gray-400 hover:text-red-600 ml-1"
                >
                  <span className="material-icons-outlined text-sm">close</span>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Rich Text Editor Area */}
        <div className="flex-1 overflow-hidden relative flex flex-col">
          <div className="flex-1 overflow-y-auto p-4 h-full flex flex-col">
            {isAiMode && (
              <div className="mb-4 rounded-lg border-2 border-blue-500 bg-grigora-50 p-4 shadow-sm animate-fade-in">
                <div className="flex items-center gap-2 mb-3">
                  <span className="material-icons-outlined text-grigora-600 text-sm">auto_awesome</span>
                  <span className="text-xs font-bold text-grigora-700 uppercase tracking-wide">
                    Describe what you want to say
                  </span>
                </div>

                <textarea
                  className="w-full p-3 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-grigora-500 outline-none mb-3 text-gray-700 resize-none bg-white shadow-sm"
                  rows={4}
                  autoFocus
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="E.g., Write a professional email to follow up on our meeting..."
                />

                <div className="flex justify-end gap-3 items-center">
                  <button
                    className="text-sm text-gray-600 font-medium hover:text-gray-900"
                    onClick={() => {
                      setIsAiMode(false);
                      setAiPrompt("");
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    className="bg-[#8B5CF6] hover:bg-[#8B5CF6] text-white px-4 py-1.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50 shadow-sm"
                    onClick={handleAiWrite}
                    disabled={!aiPrompt}
                  >
                    {isGenerating ? "Writing..." : "Generate Draft"}
                  </button>
                </div>
              </div>
            )}
            <ReactMailQuill
              ref={quillRef}
              value={body}
              onChange={setBody}
              placeholder={!isAiMode ? "Type your message here..." : ""}
              modules={modules}
              toolbarContainerId="#rich-area-text-effect-compose"
              className="full-height-pseudo"
            />
          </div>
          <style>{`
            .ql-container {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
              font-size: 14px;
              border: none !important;
              height: 100%;
              display: flex;
              flex-direction: column;
            }
            
            /* Toolbar Container - Light grey rounded container */
            #rich-area-text-effect-compose {
              display: ${showFormattingToolbar ? 'flex' : 'none'} !important;
              border: none !important;
              background-color:rgb(255, 255, 255);
              padding: 6px 10px !important;
              flex-wrap: wrap;
              align-items: center;
              gap: 0;
              box-shadow: none;
            }
            
            /* Quill will render toolbar inside the container */
            #rich-area-text-effect-compose.ql-toolbar,
            #rich-area-text-effect-compose .ql-toolbar {
              display: flex !important;
              border: none !important;
              background-color: transparent;
              padding: 0;
              margin: 0;
              gap: 0;
              width: 100%;
              border-top: 1px solid rgb(243 244 246) !important;
            }
            .ql-snow .ql-picker.ql-expanded .ql-picker-options {
              top: unset !important;
              bottom: 100%;
            }
            .ql-snow.ql-toolbar button {
              border-radius: 5px !important;
            }
            .ql-snow.ql-toolbar button:hover, .ql-snow.ql-toolbar button.ql-active {
             color: #623ce7 !important;
              background-color: rgb(243 244 246) !important;
            }
            .ql-formats{
              display: flex !important;
              flex-wrap: wrap;
              gap: 10px;
            }
            
            .ql-editor {
              min-height: 200px;
              padding: 12px;
            }
            
            .ql-editor.ql-blank::before {
              color: #9ca3af;
              font-style: normal;
            }
          `}</style>
          <div className={`${showFormattingToolbar ? 'flex' : 'hidden'}`}>
            <div id="rich-area-text-effect-compose" className={`quill-toolbar-wrapper`}>
              <span className="ql-formats">
                <select className="ql-font"></select>
                <button className="ql-bold"></button>
                <button className="ql-italic"></button>
                <button className="ql-underline"></button>
                <button className="ql-color"></button>
                <select className="ql-align"></select>
                <button className="ql-list" value="ordered"></button>
                <button className="ql-list" value="bullet"></button>
                <button className="ql-blockquote"></button>
                <button className="ql-link"></button>
                <button className="ql-image"></button>
              </span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-4 py-3 flex items-center justify-between border-t border-gray-100 relative flex-wrap gap-2">
          <div className="flex items-center gap-2 md:gap-4 flex-wrap">
            <div className="flex items-center gap-0 relative" ref={schedulePopoverRef}>
              <Button onClick={handleSend} disabled={sending || scheduling}>
                {sending ? "Sending..." : scheduling ? "Scheduling..." : "Send"}
              </Button>
              
              {/* Schedule Button (Arrow) */}
              <button
                className="p-1.5 hover:bg-gray-100 rounded text-gray-600 flex items-center transform border-l border-gray-200 ml-0"
                onClick={() => setShowSchedulePopover(!showSchedulePopover)}
                title="Schedule send"
                disabled={sending || scheduling}
              >
                <span className="material-icons-outlined text-base">arrow_drop_down</span>
              </button>

              {/* Schedule Popover */}
              {showSchedulePopover && (
                <div className="absolute bottom-full left-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 w-[calc(100vw-2rem)] md:w-auto md:min-w-[280px] max-w-[280px]">
                  <div className="text-sm font-medium text-gray-700 mb-3">Schedule send</div>
                  
                  <div className="mb-4">
                    <label className="block text-xs text-gray-600 mb-1">Date & Time</label>
                    <input
                      type="datetime-local"
                      value={scheduledDateTime}
                      onChange={(e) => setScheduledDateTime(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-grigora-500 focus:border-grigora-500 outline-none"
                      min={new Date().toISOString().slice(0, 16)}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-md"
                      onClick={() => {
                        setShowSchedulePopover(false);
                        setScheduledDateTime("");
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      className="px-3 py-1.5 text-sm bg-grigora-600 text-white rounded-md hover:bg-grigora-700 disabled:opacity-50"
                      onClick={handleSchedule}
                      disabled={!scheduledDateTime || scheduling}
                    >
                      Schedule
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 text-gray-500 relative">
              {/* Formatting Toolbar Toggle Button */}
              <button
                className={`p-1.5 hover:bg-gray-100 rounded text-gray-600 flex items-center transform ${
                  showFormattingToolbar ? "bg-gray-100 text-grigora-600" : ""
                }`}
                onClick={() => setShowFormattingToolbar(!showFormattingToolbar)}
                title="Formatting options"
              >
                <span className="material-icons-outlined text-[1.2rem]">text_format</span>
              </button>

              {/* Attach File Button */}
              <button
                className="p-1.5 hover:bg-gray-100 rounded text-gray-600 flex items-center transform"
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
              >
                <span className="material-icons-outlined text-[1.2rem]">attach_file</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />

            </div>

            <div className="h-5 w-px bg-gray-200"></div>

            {/* Compose with AI Button */}
            <button
              onClick={() => setIsAiMode(!isAiMode)}
              className={`p-1.5 rounded flex items-center justify-center transition-colors ${
                isAiMode
                  ? "bg-grigora-100 text-grigora-600"
                  : "hover:bg-gray-100 text-grigora-500"
              }`}
              title="Help me write"
            >
              <span className="material-icons-outlined text-[1.2rem]">auto_awesome</span>
            </button>
          </div>

          <button className="text-gray-400 hover:text-gray-600 flex items-center" onClick={handleClose}>
            <span className="material-icons-outlined text-xl">delete</span>
          </button>
        </div>
      </div>
    </div>
  );
};
